import { useEffect, useRef, useCallback } from 'react'

interface InfiniteScrollContainerProps {
  hasMore: boolean
  isLoading: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
  threshold?: number
  rootMargin?: string
  children?: React.ReactNode
}

export function InfiniteScrollContainer({
  hasMore,
  isLoading,
  isFetchingMore,
  onLoadMore,
  threshold = 0.1,
  rootMargin = '0px',
  children,
}: InfiniteScrollContainerProps) {
  const observerTarget = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasMore && !isLoading && !isFetchingMore) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, isFetchingMore, onLoadMore]
  )

  useEffect(() => {
    const element = observerTarget.current
    if (!element) return

    const observer = new IntersectionObserver(handleObserver, {
      threshold,
      rootMargin,
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [handleObserver, threshold, rootMargin])

  return (
    <>
      {children}
      {hasMore && <div ref={observerTarget} />}
    </>
  )
}
